/**
 * useFinancialStream - Hook for consuming Financial Deep Dive SSE events
 *
 * Handles real-time streaming updates from the financial analysis backend:
 * - hard_data_start/ready: Market data snapshot
 * - bull_team_start/complete, bear_team_start/complete: Team progress
 * - agent_start/complete: Individual agent progress
 * - debate_start/complete: Adversarial debate
 * - done: Final kill sheet
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '@/lib/api';
import type {
  FinancialStreamingState,
  FinancialAgentProgress,
  MediatorCompilation,
  FinancialResearchDepth,
} from '@/components/verdict/FinancialLogicTree';
import type { FinancialKillSheet, AnchoredSnapshot } from '@/components/verdict/FinancialAnalysisPanel';

// Agent role configs from backend
const BULL_ROLE_NAMES: Record<string, string> = {
  growth_strategist: 'Growth Strategist',
  momentum_analyst: 'Momentum Analyst',
  innovation_scout: 'Innovation Scout',
  sector_specialist: 'Sector Specialist',
  capital_optimizer: 'Capital Optimizer',
};

const BEAR_ROLE_NAMES: Record<string, string> = {
  forensic_accountant: 'Forensic Accountant',
  risk_analyst: 'Risk Analyst',
  valuation_skeptic: 'Valuation Skeptic',
  short_seller: 'Short Seller',
  macro_bear: 'Macro Bear',
};

// Provider inference from model name
function inferProvider(model: string): 'anthropic' | 'openai' | 'google' | 'deepseek' {
  const lower = model.toLowerCase();
  if (lower.includes('claude') || lower.includes('anthropic')) return 'anthropic';
  if (lower.includes('gpt') || lower.includes('openai')) return 'openai';
  if (lower.includes('gemini') || lower.includes('google')) return 'google';
  if (lower.includes('deepseek')) return 'deepseek';
  return 'openai'; // default
}

/** Configuration for free-tier financial analysis (uses fingerprint tracking) */
export interface FreeTierFinancialConfig {
  fingerprintId?: string;
  email?: string;
  topic: string;
  turnstileToken: string;
  tasks: Array<{ name: string; description: string; execution_mode?: string }>;
}

interface UseFinancialStreamOptions {
  onComplete?: (killSheet: FinancialKillSheet) => void;
  onError?: (error: string) => void;
}

interface UseFinancialStreamReturn {
  state: FinancialStreamingState;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  startAnalysis: (ticker: string | null, depth: FinancialResearchDepth, question?: string, options?: { portfolioContext?: boolean }) => void;
  /** Start analysis using free-tier endpoint with fingerprint tracking */
  startFreeTierAnalysis: (config: FreeTierFinancialConfig) => void;
  disconnect: () => void;
  reset: () => void;
}

const initialState: FinancialStreamingState = {
  phase: 'idle',
  ticker: '',
  depth: 'fast',
  bullAgents: [],
  bearAgents: [],
  debateMessages: [],
  debateRound: 0,
  agreementScore: 0,
};

export function useFinancialStream(options?: UseFinancialStreamOptions): UseFinancialStreamReturn {
  const [state, setState] = useState<FinancialStreamingState>(initialState);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const onCompleteRef = useRef(options?.onComplete);
  const onErrorRef = useRef(options?.onError);
  onCompleteRef.current = options?.onComplete;
  onErrorRef.current = options?.onError;

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
    setIsConnecting(false);
  }, []);

  const reset = useCallback(() => {
    disconnect();
    setState(initialState);
    setError(null);
  }, [disconnect]);

  const startAnalysis = useCallback(
    async (ticker: string | null, depth: FinancialResearchDepth, question?: string, options?: { portfolioContext?: boolean }) => {
      // Reset state first
      setState({
        ...initialState,
        ticker: ticker?.toUpperCase() || '',  // Will be updated when intent_parsed event arrives
        depth,
        phase: 'fetching_data',
      });
      setError(null);
      setIsConnecting(true);

      const url = `${API_BASE_URL}/api/v1/financial/analyze/stream`;

      // Get auth token from localStorage
      const token = localStorage.getItem('access_token');

      try {
        // POST request with streaming response
        // ticker can be null - backend uses LLM to extract from question
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({
            ticker: ticker?.toUpperCase() || null,
            depth,
            question,
            portfolio_context: options?.portfolioContext || false,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        setIsConnected(true);
        setIsConnecting(false);

        // Process SSE stream using fetch
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = async () => {
          // Persist across chunks so large SSE events spanning multiple
          // TCP segments are reassembled correctly.
          let eventType = '';
          let eventData = '';

          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });

              // Parse SSE events from buffer
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // Keep incomplete line in buffer

              for (const line of lines) {
                if (line.startsWith('event: ')) {
                  eventType = line.slice(7).trim();
                } else if (line.startsWith('data: ')) {
                  eventData = line.slice(6);
                } else if (line === '' && eventType && eventData) {
                  // Complete event - process it
                  handleSSEEvent(eventType, eventData, setState, disconnect, onCompleteRef, onErrorRef);
                  eventType = '';
                  eventData = '';
                }
              }
            }
          } catch (e) {
            console.error('Stream reading error:', e);
            setError('Connection lost');
          } finally {
            setIsConnected(false);
          }
        };

        processStream();
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'Failed to start analysis';
        setError(errorMsg);
        setIsConnecting(false);
        onErrorRef.current?.(errorMsg);
      }
    },
    [disconnect]
  );

  // Start analysis using free-tier endpoint with fingerprint tracking
  const startFreeTierAnalysis = useCallback(
    async (config: FreeTierFinancialConfig) => {
      // Reset state first
      setState({
        ...initialState,
        ticker: '', // Will be extracted from topic by backend
        depth: 'fast', // Free tier always uses fast
        phase: 'fetching_data',
      });
      setError(null);
      setIsConnecting(true);

      const url = `${API_BASE_URL}/api/v1/free-report/stream`;

      try {
        // POST request to free-tier streaming endpoint
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            topic: config.topic,
            turnstile_token: config.turnstileToken,
            tasks: config.tasks,
            fingerprint_id: config.fingerprintId,
            email: config.email,
            mode: 'financial', // Force financial mode
            intensity: 'fast', // Free tier always fast
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        setIsConnected(true);
        setIsConnecting(false);

        // Process SSE stream using fetch (same as authenticated flow)
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = async () => {
          // Persist across chunks so large SSE events spanning multiple
          // TCP segments are reassembled correctly.
          let eventType = '';
          let eventData = '';

          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });

              // Parse SSE events from buffer
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // Keep incomplete line in buffer

              for (const line of lines) {
                if (line.startsWith('event: ')) {
                  eventType = line.slice(7).trim();
                } else if (line.startsWith('data: ')) {
                  eventData = line.slice(6);
                } else if (line === '' && eventType && eventData) {
                  // Complete event - process it
                  handleSSEEvent(eventType, eventData, setState, disconnect, onCompleteRef, onErrorRef);
                  eventType = '';
                  eventData = '';
                }
              }
            }
          } catch (e) {
            console.error('Stream reading error:', e);
            setError('Connection lost');
          } finally {
            setIsConnected(false);
          }
        };

        processStream();
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'Failed to start free-tier analysis';
        setError(errorMsg);
        setIsConnecting(false);
        onErrorRef.current?.(errorMsg);
      }
    },
    [disconnect]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => disconnect();
  }, [disconnect]);

  return {
    state,
    isConnected,
    isConnecting,
    error,
    startAnalysis,
    startFreeTierAnalysis,
    disconnect,
    reset,
  };
}

// Helper function to handle a single SSE event
function handleSSEEvent(
  eventType: string,
  eventData: string,
  setState: React.Dispatch<React.SetStateAction<FinancialStreamingState>>,
  disconnect: () => void,
  onCompleteRef: React.MutableRefObject<((killSheet: FinancialKillSheet) => void) | undefined>,
  onErrorRef: React.MutableRefObject<((error: string) => void) | undefined>
) {
  try {
    const data = JSON.parse(eventData);
    console.log('[Financial SSE] Event:', eventType, data);

    switch (eventType) {
      case 'intent_parsed':
        // LLM intent parser has extracted ticker and classified the query
        console.log('[Financial SSE] Intent parsed:', data);
        if (data.ticker) {
          setState((prev) => ({
            ...prev,
            ticker: data.ticker,
            phase: 'fetching_data',
          }));
        }
        break;

      case 'fallback':
        // Query is not suitable for Financial Deep Dive - should use general research
        console.log('[Financial SSE] Fallback triggered:', data.reason);
        setState((prev) => ({
          ...prev,
          phase: 'error',
          fallbackData: {
            reason: data.reason || 'Query does not require Financial Deep Dive',
            suggestion: data.suggestion || 'For stock-specific analysis, try asking about a particular company like "Analyze NVDA" or "Should I buy Apple stock?"',
            originalQuery: data.original_query || '',
          },
        }));
        onErrorRef.current?.(data.reason || 'Query should use general research mode');
        disconnect();
        break;

      case 'start':
        setState((prev) => ({
          ...prev,
          ticker: data.ticker,
          depth: data.depth as FinancialResearchDepth,
          phase: 'fetching_data',
        }));
        break;

      case 'hard_data_ready':
        console.log('[Financial SSE] hard_data_ready snapshot:', data.snapshot || data.snapshot_a);
        setState((prev) => {
          // Comparison mode: snapshot_a and snapshot_b
          if (prev.isComparison && data.snapshot_a) {
            return {
              ...prev,
              hardDataSnapshot: data.snapshot_a as AnchoredSnapshot,
              comparisonSnapshotA: data.snapshot_a as AnchoredSnapshot,
              comparisonSnapshotB: data.snapshot_b as AnchoredSnapshot,
            };
          }
          // Single-ticker mode
          return {
            ...prev,
            hardDataSnapshot: data.snapshot as AnchoredSnapshot,
          };
        });
        break;

      case 'bull_team_start': {
        const agentCount = data.agent_count || 0;
        const bullAgents: FinancialAgentProgress[] = Array.from({ length: agentCount }, (_, i) => ({
          side: 'bull' as const,
          index: i,
          roleId: '',
          roleName: '',
          model: '',
          provider: 'openai' as const,
          status: 'idle' as const,
        }));
        setState((prev) => ({
          ...prev,
          phase: 'bull_research',
          bullAgents,
        }));
        break;
      }

      case 'bear_team_start': {
        const agentCount = data.agent_count || 0;
        const bearAgents: FinancialAgentProgress[] = Array.from({ length: agentCount }, (_, i) => ({
          side: 'bear' as const,
          index: i,
          roleId: '',
          roleName: '',
          model: '',
          provider: 'openai' as const,
          status: 'idle' as const,
        }));
        setState((prev) => ({
          ...prev,
          phase: 'bear_research',
          bearAgents,
        }));
        break;
      }

      case 'agent_start': {
        const { side, agent_index, agent_role, model, ticker: agentTicker } = data;
        const roleNames = side === 'bull' ? BULL_ROLE_NAMES : BEAR_ROLE_NAMES;
        const roleName = roleNames[agent_role] || agent_role;
        const provider = inferProvider(model);

        setState((prev) => {
          // V2: agents belong to a specific stock, mapped to bull/bearAgents by active stock
          if (prev.isComparison && agentTicker) {
            const isStockA = agentTicker === prev.comparisonTickerA;
            const targetKey = isStockA ? 'bullAgents' : 'bearAgents';
            const agents = [...prev[targetKey]];
            // In v2, bull agents come first, bear agents after in the combined array
            const bullCount = isStockA
              ? (prev.stockABullAgents?.length || 0)
              : (prev.stockBBullAgents?.length || 0);
            const realIndex = side === 'bull' ? agent_index : bullCount + agent_index;
            if (agents[realIndex]) {
              agents[realIndex] = {
                ...agents[realIndex],
                roleId: agent_role,
                roleName: `${roleName}`,
                model,
                provider,
                status: 'researching',
              };
            }
            return { ...prev, [targetKey]: agents };
          }

          // V1 / single-stock: original behavior
          const agents = side === 'bull' ? [...prev.bullAgents] : [...prev.bearAgents];
          if (agents[agent_index]) {
            agents[agent_index] = {
              ...agents[agent_index],
              roleId: agent_role,
              roleName,
              model,
              provider,
              status: 'researching',
            };
          }
          return side === 'bull'
            ? { ...prev, bullAgents: agents }
            : { ...prev, bearAgents: agents };
        });
        break;
      }

      case 'agent_retry': {
        // Handle agent retry when a model fails and we're trying a fallback
        const { side, agent_index, agent_role, failed_model, retry_model, attempt, max_attempts, ticker: retryTicker } = data;
        const provider = inferProvider(retry_model);
        const roleNames = side === 'bull' ? BULL_ROLE_NAMES : BEAR_ROLE_NAMES;
        const roleName = roleNames[agent_role] || agent_role;

        console.log(`[Financial SSE] Agent retry: ${roleName} failed with ${failed_model}, trying ${retry_model} (attempt ${attempt}/${max_attempts})`);

        setState((prev) => {
          // V2: route to correct stock's agent array
          if (prev.isComparison && retryTicker) {
            const isStockA = retryTicker === prev.comparisonTickerA;
            const targetKey = isStockA ? 'bullAgents' : 'bearAgents';
            const agents = [...prev[targetKey]];
            const bullCount = isStockA
              ? (prev.stockABullAgents?.length || 0)
              : (prev.stockBBullAgents?.length || 0);
            const realIndex = side === 'bull' ? agent_index : bullCount + agent_index;
            if (agents[realIndex]) {
              agents[realIndex] = {
                ...agents[realIndex],
                model: retry_model,
                provider,
                status: 'researching',
                retryCount: (agents[realIndex].retryCount || 0) + 1,
                lastFailedModel: failed_model,
              };
            }
            return { ...prev, [targetKey]: agents };
          }

          const agents = side === 'bull' ? [...prev.bullAgents] : [...prev.bearAgents];
          if (agents[agent_index]) {
            agents[agent_index] = {
              ...agents[agent_index],
              model: retry_model,  // Update to the new model being tried
              provider,
              status: 'researching',  // Still researching with fallback
              retryCount: (agents[agent_index].retryCount || 0) + 1,
              lastFailedModel: failed_model,
            };
          }
          return side === 'bull'
            ? { ...prev, bullAgents: agents }
            : { ...prev, bearAgents: agents };
        });
        break;
      }

      case 'agent_complete': {
        const { side, agent_index, latency, success, citation_count, citations, output_preview, model, ticker: completeTicker } = data;

        setState((prev) => {
          // V2: route to correct stock's agent array
          if (prev.isComparison && completeTicker) {
            const isStockA = completeTicker === prev.comparisonTickerA;
            const targetKey = isStockA ? 'bullAgents' : 'bearAgents';
            const agents = [...prev[targetKey]];
            const bullCount = isStockA
              ? (prev.stockABullAgents?.length || 0)
              : (prev.stockBBullAgents?.length || 0);
            const realIndex = side === 'bull' ? agent_index : bullCount + agent_index;
            if (agents[realIndex]) {
              agents[realIndex] = {
                ...agents[realIndex],
                status: success ? 'complete' : 'error',
                citationCount: citation_count,
                citations: citations || [],
                latency,
                output: output_preview,
                model: model || agents[realIndex].model,
                provider: model ? inferProvider(model) : agents[realIndex].provider,
              };
            }
            return { ...prev, [targetKey]: agents };
          }

          const agents = side === 'bull' ? [...prev.bullAgents] : [...prev.bearAgents];
          if (agents[agent_index]) {
            agents[agent_index] = {
              ...agents[agent_index],
              status: success ? 'complete' : 'error',
              citationCount: citation_count,
              citations: citations || [], // Full citation details with URLs and timestamps
              latency,
              output: output_preview,
              // Update model if provided (in case it changed due to retry)
              model: model || agents[agent_index].model,
              provider: model ? inferProvider(model) : agents[agent_index].provider,
            };
          }
          return side === 'bull'
            ? { ...prev, bullAgents: agents }
            : { ...prev, bearAgents: agents };
        });
        break;
      }

      case 'debate_start':
        setState((prev) => ({
          ...prev,
          phase: 'debating',
          debateRound: 0,
          debateMessages: [{
            side: 'mediator' as const,
            round: 0,
            text: data.message || 'Beginning adversarial debate...',
            timestamp: Date.now(),
          }],
        }));
        break;

      case 'debate_round_start':
        setState((prev) => ({
          ...prev,
          debateRound: data.round || prev.debateRound + 1,
          debateMessages: [
            ...prev.debateMessages,
            {
              side: 'mediator' as const,
              round: data.round,
              text: data.message || `Round ${data.round}: Exchanging critiques...`,
              timestamp: Date.now(),
            },
          ],
        }));
        break;

      case 'debate_bull_critique':
        setState((prev) => ({
          ...prev,
          debateMessages: [
            ...prev.debateMessages,
            {
              side: 'bull' as const,
              round: data.round,
              text: data.critique || 'Bull critique submitted.',
              timestamp: Date.now(),
            },
          ],
        }));
        break;

      case 'debate_bear_critique':
        setState((prev) => ({
          ...prev,
          debateMessages: [
            ...prev.debateMessages,
            {
              side: 'bear' as const,
              round: data.round,
              text: data.critique || 'Bear critique submitted.',
              timestamp: Date.now(),
            },
          ],
        }));
        break;

      case 'debate_round_complete':
        // Round finished - no additional message needed, critiques already shown
        break;

      // Comparison mode debate events
      case 'debate_advocate_a':
        setState((prev) => ({
          ...prev,
          debateMessages: [
            ...prev.debateMessages,
            {
              side: 'bull' as const,
              round: data.round,
              text: data.argument || `${data.ticker} advocate argument.`,
              timestamp: Date.now(),
              label: data.ticker,
            },
          ],
        }));
        break;

      case 'debate_advocate_b':
        setState((prev) => ({
          ...prev,
          debateMessages: [
            ...prev.debateMessages,
            {
              side: 'bear' as const,
              round: data.round,
              text: data.argument || `${data.ticker} advocate argument.`,
              timestamp: Date.now(),
              label: data.ticker,
            },
          ],
        }));
        break;

      case 'debate_consensus_check':
        if (data.consensus_reached) {
          setState((prev) => ({
            ...prev,
            debateMessages: [
              ...prev.debateMessages,
              {
                side: 'mediator' as const,
                round: data.round,
                text: `Consensus reached (confidence: ${(data.confidence * 100).toFixed(0)}%): ${data.reason}`,
                timestamp: Date.now(),
              },
            ],
          }));
        }
        break;

      case 'debate_mediator_start':
        setState((prev) => ({
          ...prev,
          debateMessages: [
            ...prev.debateMessages,
            {
              side: 'mediator' as const,
              round: prev.debateRound,
              text: data.message || 'Mediator is rendering final verdict...',
              timestamp: Date.now(),
            },
          ],
        }));
        break;

      case 'debate_complete': {
        const confidence = Math.round((data.confidence || 0.85) * 100);
        setState((prev) => ({
          ...prev,
          phase: 'compiling',
          agreementScore: confidence,
          debateMessages: [
            ...prev.debateMessages,
            {
              side: 'mediator' as const,
              round: prev.debateRound,
              text: `Verdict reached. ${data.winning_side === 'bull' ? 'Bull' : 'Bear'} case prevails with ${confidence}% confidence.`,
              timestamp: Date.now(),
            },
          ],
        }));
        break;
      }

      case 'done': {
        const killSheet = data.kill_sheet as FinancialKillSheet;
        const mediatorCompilation: MediatorCompilation = {
          bullIntensity: killSheet.bull_intensity || (killSheet.winning_side === 'bull' ? 65 : 35),
          bearIntensity: killSheet.bear_risk || (killSheet.winning_side === 'bear' ? 65 : 35),
          bullArguments: killSheet.catalysts || [killSheet.bull_thesis || 'Bull thesis'],
          bearArguments: killSheet.risks || [killSheet.bear_thesis || 'Bear thesis'],
          verdict: killSheet.kill_sheet_reason || killSheet.decisive_factor || 'Analysis complete.',
        };

        setState((prev) => ({
          ...prev,
          phase: 'concluded',
          killSheet,
          mediatorCompilation,
          hardDataSnapshot: data.anchored_snapshot || prev.hardDataSnapshot,
          // Convergence & echo chamber data for frontend visualization
          convergenceTrajectory: (data.convergence_trajectory as number[]) || [],
          initialDivergence: (data.initial_divergence as number) || 0,
          echoChamberedDetected: (data.echo_chamber_detected as boolean) || false,
          totalCreditsUsed: typeof data.total_cost === 'number' ? data.total_cost : undefined,
        }));

        onCompleteRef.current?.(killSheet);
        disconnect();
        break;
      }

      case 'insufficient_credits': {
        const errorMsg = `Insufficient credits: need ${data.required}, have ${data.available}`;
        setState((prev) => ({
          ...prev,
          phase: 'error',
          error: errorMsg,
          insufficientCredits: {
            required: data.required,
            available: data.available,
          },
        }));
        onErrorRef.current?.(errorMsg);
        disconnect();
        break;
      }

      case 'error': {
        const errorMsg = data.error || 'Unknown error';
        setState((prev) => ({
          ...prev,
          phase: 'error',
          error: errorMsg,
        }));
        onErrorRef.current?.(errorMsg);
        disconnect();
        break;
      }

      // =====================================================================
      // COMPARISON MODE EVENTS
      // =====================================================================
      case 'comparison_start':
        setState((prev) => ({
          ...prev,
          ticker: `${data.ticker_a} vs ${data.ticker_b}`,
          depth: data.depth as FinancialResearchDepth,
          phase: 'fetching_data',
          isComparison: true,
          comparisonTickerA: data.ticker_a,
          comparisonTickerB: data.ticker_b,
          agentsPerStock: data.agents_per_stock || 0,
        }));
        break;

      case 'comparison_limited':
        // 3+ tickers were requested but only 2 are supported
        console.warn('[Financial SSE] Comparison limited:', data.message, 'Dropped:', data.dropped_tickers);
        setState((prev) => ({
          ...prev,
          warnings: [...(prev.warnings || []), data.message],
        }));
        break;

      // Per-stock analysis events
      case 'stock_analysis_start': {
        const stockTicker = data.ticker;
        const bullCount = data.bull_count || 0;
        const bearCount = data.bear_count || 0;

        setState((prev) => {
          const isStockA = stockTicker === prev.comparisonTickerA;
          const totalPerStock = bullCount + bearCount;

          const newBullAgents: FinancialAgentProgress[] = Array.from({ length: bullCount }, (_, i) => ({
            side: 'bull' as const,
            index: i,
            roleId: '',
            roleName: '',
            model: '',
            provider: 'openai' as const,
            status: 'idle' as const,
          }));
          const newBearAgents: FinancialAgentProgress[] = Array.from({ length: bearCount }, (_, i) => ({
            side: 'bear' as const,
            index: i,
            roleId: '',
            roleName: '',
            model: '',
            provider: 'openai' as const,
            status: 'idle' as const,
          }));

          if (isStockA) {
            return {
              ...prev,
              phase: 'bull_research',
              activeStockTicker: stockTicker,
              stockABullAgents: newBullAgents,
              stockABearAgents: newBearAgents,
              bullAgents: [...newBullAgents, ...newBearAgents],
              agentsPerStock: totalPerStock,
            };
          } else {
            return {
              ...prev,
              phase: 'bear_research',
              activeStockTicker: stockTicker,
              stockBBullAgents: newBullAgents,
              stockBBearAgents: newBearAgents,
              bearAgents: [...newBullAgents, ...newBearAgents],
              agentsPerStock: totalPerStock,
            };
          }
        });
        break;
      }

      case 'stock_synthesis_start':
        // Synthesis LLM call running for a stock
        break;

      case 'stock_synthesis_complete':
        // Synthesis complete for a stock
        break;

      case 'stock_analysis_complete':
        // All agents + synthesis done for a stock
        break;

      case 'team_a_start': {
        const agentCount = data.agent_count || 0;
        const teamAgents: FinancialAgentProgress[] = Array.from({ length: agentCount }, (_, i) => ({
          side: 'bull' as const,  // team_a maps to bull in UI
          index: i,
          roleId: '',
          roleName: '',
          model: '',
          provider: 'openai' as const,
          status: 'idle' as const,
        }));
        setState((prev) => ({
          ...prev,
          phase: 'bull_research',
          bullAgents: teamAgents,
        }));
        break;
      }

      case 'team_a_complete':
        // Team A done, bear_research phase starts next
        break;

      case 'team_b_start': {
        const agentCount = data.agent_count || 0;
        const teamAgents: FinancialAgentProgress[] = Array.from({ length: agentCount }, (_, i) => ({
          side: 'bear' as const,  // team_b maps to bear in UI
          index: i,
          roleId: '',
          roleName: '',
          model: '',
          provider: 'openai' as const,
          status: 'idle' as const,
        }));
        setState((prev) => ({
          ...prev,
          phase: 'bear_research',
          bearAgents: teamAgents,
        }));
        break;
      }

      case 'team_b_complete':
        break;

      case 'comparison_mediator_start':
        setState((prev) => ({
          ...prev,
          phase: 'compiling',
        }));
        break;

      case 'comparison_done': {
        // comparison_done carries a comparison_kill_sheet instead of kill_sheet
        const compKillSheet = data.comparison_kill_sheet;
        // Map comparison kill sheet to FinancialKillSheet shape for UI reuse
        const killSheet: FinancialKillSheet = {
          ticker: `${compKillSheet.ticker_a} vs ${compKillSheet.ticker_b}`,
          analysis_date: compKillSheet.analysis_date,
          winning_side: compKillSheet.recommended_ticker === compKillSheet.ticker_a ? 'bull' : 'bear',
          confidence: compKillSheet.confidence,
          bull_thesis: compKillSheet.ticker_a_thesis,
          bear_thesis: compKillSheet.ticker_b_thesis,
          kill_sheet_reason: compKillSheet.comparison_reason,
          decisive_factor: compKillSheet.comparison_reason,
          rejected_arguments: compKillSheet.rejected_arguments || [],
          caveats: compKillSheet.caveats || '',
          data_sources: compKillSheet.data_sources || {},
          research_depth: compKillSheet.research_depth,
          total_agents: compKillSheet.total_agents,
          debate_rounds: compKillSheet.debate_rounds,
          stance: compKillSheet.recommended_ticker === compKillSheet.ticker_a
            ? compKillSheet.stance_a : compKillSheet.stance_b,
          bull_intensity: 50,
          bear_risk: 50,
          catalysts: compKillSheet.catalysts_a || [],
          risks: compKillSheet.risks_b || [],
          watch_items: compKillSheet.watch_items || [],
        };

        const mediatorCompilation: MediatorCompilation = {
          bullIntensity: 50,
          bearIntensity: 50,
          bullArguments: [compKillSheet.ticker_a_thesis || ''],
          bearArguments: [compKillSheet.ticker_b_thesis || ''],
          verdict: compKillSheet.comparison_reason || 'Comparison complete.',
        };

        setState((prev) => ({
          ...prev,
          phase: 'concluded',
          killSheet,
          mediatorCompilation,
          hardDataSnapshot: data.snapshot_a || prev.hardDataSnapshot,
          // Store comparison-specific data
          comparisonKillSheet: compKillSheet,
          comparisonSnapshotA: data.snapshot_a,
          comparisonSnapshotB: data.snapshot_b,
          totalCreditsUsed: typeof data.total_cost === 'number' ? data.total_cost : undefined,
        }));

        onCompleteRef.current?.(killSheet);
        disconnect();
        break;
      }
    }
  } catch (e) {
    console.error(`Failed to parse ${eventType} event:`, e);
  }
}

